import { useState, useEffect, useCallback } from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from 'recharts';
import TopNav from '../components/TopNav';
import { api, setAuthTokenProvider, type SessionResult } from '../api/client';
import { useAuth } from '../hooks/useAuth';
import { detectProvider } from '../utils/auth-utils';
import type { AdminStats, AdminRecruiter, AlarmStatus } from 'aliv-shared';

type Tab = 'overview' | 'users' | 'health';

export default function AdminDashboard() {
  const { user, signOut, getAccessToken, isAdmin } = useAuth();
  const [tab, setTab] = useState<Tab>('overview');

  // Overview state
  const [stats, setStats] = useState<AdminStats | null>(null);
  const [recentSessions, setRecentSessions] = useState<SessionResult[]>([]);

  // Users state
  const [recruiters, setRecruiters] = useState<AdminRecruiter[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [togglingUser, setTogglingUser] = useState<string | null>(null);

  // Health state
  const [alarms, setAlarms] = useState<AlarmStatus[]>([]);
  const [metrics, setMetrics] = useState<Record<string, number>>({});

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const provider = detectProvider(user?.username);
  const { email: authEmail } = useAuth();
  const email = authEmail || user?.signInDetails?.loginId || '';

  useEffect(() => {
    setAuthTokenProvider(getAccessToken);
  }, [getAccessToken]);

  const fetchOverview = useCallback(async () => {
    setError(null);
    try {
      const [statsData, sessionsData] = await Promise.all([
        api.adminStats(),
        api.adminListSessions(20),
      ]);
      setStats(statsData);
      setRecentSessions(sessionsData.sessions);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load overview');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchUsers = useCallback(async () => {
    setError(null);
    try {
      const data = await api.adminListRecruiters();
      setRecruiters(data.recruiters);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load users');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchHealth = useCallback(async () => {
    setError(null);
    try {
      const data = await api.adminHealth();
      setAlarms(data.alarms);
      setMetrics(data.metrics);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load health');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchData = useCallback(() => {
    setLoading(true);
    if (tab === 'overview') return fetchOverview();
    if (tab === 'users') return fetchUsers();
    return fetchHealth();
  }, [tab, fetchOverview, fetchUsers, fetchHealth]);

  useEffect(() => {
    void fetchData();
  }, [fetchData]);

  useEffect(() => {
    const interval = setInterval(() => void fetchData(), 30_000);
    return () => clearInterval(interval);
  }, [fetchData]);

  async function handleToggle(recruiterId: string, enabled: boolean) {
    setTogglingUser(recruiterId);
    try {
      await api.adminToggleRecruiter(recruiterId, enabled);
      setRecruiters((prev) =>
        prev.map((r) => (r.username === recruiterId ? { ...r, enabled } : r)),
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to toggle user');
    } finally {
      setTogglingUser(null);
    }
  }

  const filteredRecruiters = recruiters.filter((r) => {
    const email = r.attributes?.email ?? r.username;
    return email.toLowerCase().includes(searchQuery.toLowerCase());
  });

  const statusData = stats
    ? [
        { name: 'Pending', value: stats.byStatus['PENDING'] || 0, color: '#b45309' },
        { name: 'Passed', value: stats.byStatus['SUCCEEDED'] || 0, color: '#16a34a' },
        { name: 'Failed', value: stats.byStatus['FAILED'] || 0, color: '#b91c1c' },
      ]
    : [];

  function formatDate(iso: string) {
    return new Date(iso).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  }

  function alarmStateClass(state: string) {
    if (state === 'OK') return 'ok';
    if (state === 'ALARM') return 'alarm';
    return 'insufficient';
  }

  return (
    <>
      <TopNav
        email={email}
        provider={provider}
        isAdmin={isAdmin}
        onSignOut={() => void signOut()}
      />

      <div className="dashboard">
        <h1 className="dashboard-title">Admin Dashboard</h1>
        <p className="dashboard-subtitle">
          System overview, user management, and health monitoring.
        </p>

        <div className="admin-tabs">
          <button
            className={`admin-tab ${tab === 'overview' ? 'admin-tab--active' : ''}`}
            onClick={() => setTab('overview')}
          >
            Overview
          </button>
          <button
            className={`admin-tab ${tab === 'users' ? 'admin-tab--active' : ''}`}
            onClick={() => setTab('users')}
          >
            Users
          </button>
          <button
            className={`admin-tab ${tab === 'health' ? 'admin-tab--active' : ''}`}
            onClick={() => setTab('health')}
          >
            System Health
          </button>
        </div>

        {error && <p className="error-msg">{error}</p>}

        {tab === 'overview' && (
          <>
            {stats && (
              <div className="stats-row">
                <div className="stat-card stat-card-total">
                  <div className="stat-number">{stats.totalRecruiters}</div>
                  <div className="stat-label">Total Users</div>
                </div>
                <div className="stat-card stat-card-pending">
                  <div className="stat-number">{stats.totalSessions}</div>
                  <div className="stat-label">Total Sessions</div>
                </div>
                <div className="stat-card stat-card-pass">
                  <div className="stat-number">{(stats.passRate * 100).toFixed(1)}%</div>
                  <div className="stat-label">Pass Rate</div>
                </div>
                <div className="stat-card stat-card-fail">
                  <div className="stat-number">{stats.avgConfidence.toFixed(1)}%</div>
                  <div className="stat-label">Avg Confidence</div>
                </div>
              </div>
            )}

            {statusData.length > 0 && (
              <div className="chart-container">
                <h3 className="chart-title">Sessions by Status</h3>
                <ResponsiveContainer width="100%" height={280}>
                  <BarChart data={statusData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                    <XAxis dataKey="name" tick={{ fontSize: 13 }} />
                    <YAxis tick={{ fontSize: 13 }} />
                    <Tooltip />
                    <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                      {statusData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}

            {loading ? (
              <div className="card">
                <p>Loading sessions...</p>
              </div>
            ) : (
              <div className="card sessions-card">
                <div className="sessions-header">
                  <h2>Recent Sessions</h2>
                </div>
                <div className="sessions-table-wrap">
                  <table className="sessions-table">
                    <thead>
                      <tr>
                        <th>Candidate</th>
                        <th>Status</th>
                        <th>Confidence</th>
                        <th>Created</th>
                      </tr>
                    </thead>
                    <tbody>
                      {recentSessions.map((s) => (
                        <tr key={s.sessionId}>
                          <td>{s.candidateName}</td>
                          <td>
                            <span
                              className={`badge badge-${s.status === 'SUCCEEDED' && s.passed ? 'pass' : s.status === 'SUCCEEDED' && !s.passed ? 'fail' : s.status === 'FAILED' ? 'fail' : s.status === 'EXPIRED' ? 'expired' : 'pending'}`}
                            >
                              {s.status === 'SUCCEEDED'
                                ? s.passed
                                  ? 'Passed'
                                  : 'Low Confidence'
                                : s.status}
                            </span>
                          </td>
                          <td>{s.confidence != null ? `${s.confidence.toFixed(1)}%` : '--'}</td>
                          <td>{formatDate(s.createdAt)}</td>
                        </tr>
                      ))}
                      {recentSessions.length === 0 && (
                        <tr>
                          <td colSpan={4} style={{ textAlign: 'center', padding: '2rem' }}>
                            No sessions found.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </>
        )}

        {tab === 'users' && (
          <>
            <div className="admin-search">
              <input
                className="input"
                type="text"
                placeholder="Search by email..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>

            {loading ? (
              <div className="card">
                <p>Loading users...</p>
              </div>
            ) : (
              <div className="card sessions-card">
                <div className="sessions-table-wrap">
                  <table className="sessions-table">
                    <thead>
                      <tr>
                        <th>Email</th>
                        <th>Provider</th>
                        <th>Sessions</th>
                        <th>Status</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredRecruiters.map((r) => {
                        const email = r.attributes?.email ?? r.username;
                        let provider = 'Email';
                        try {
                          const ids = r.attributes?.identities;
                          if (ids) {
                            const parsed = JSON.parse(ids);
                            if (Array.isArray(parsed) && parsed[0]?.providerName) {
                              provider = parsed[0].providerName;
                            }
                          }
                        } catch {
                          /* ignore */
                        }

                        return (
                          <tr key={r.username}>
                            <td>{email}</td>
                            <td>{provider}</td>
                            <td>—</td>
                            <td>
                              <span className={`badge ${r.enabled ? 'badge-pass' : 'badge-fail'}`}>
                                {r.enabled ? 'Enabled' : 'Disabled'}
                              </span>
                            </td>
                            <td>
                              <button
                                className={`toggle-btn ${r.enabled ? 'toggle-btn--disable' : 'toggle-btn--enable'}`}
                                disabled={togglingUser === r.username}
                                onClick={() => void handleToggle(r.username, !r.enabled)}
                              >
                                {togglingUser === r.username
                                  ? '...'
                                  : r.enabled
                                    ? 'Disable'
                                    : 'Enable'}
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                      {filteredRecruiters.length === 0 && (
                        <tr>
                          <td colSpan={5} style={{ textAlign: 'center', padding: '2rem' }}>
                            No users found.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </>
        )}

        {tab === 'health' && (
          <>
            {Object.keys(metrics).length > 0 && (
              <div className="stats-row">
                <div className="stat-card stat-card-total">
                  <div className="stat-number">{metrics['invocations'] ?? 0}</div>
                  <div className="stat-label">Invocations</div>
                </div>
                <div className="stat-card stat-card-fail">
                  <div className="stat-number">{metrics['errors'] ?? 0}</div>
                  <div className="stat-label">Errors</div>
                </div>
                <div className="stat-card stat-card-pending">
                  <div className="stat-number">
                    {metrics['invocations']
                      ? (((metrics['errors'] ?? 0) / metrics['invocations']) * 100).toFixed(2)
                      : '0.00'}
                    %
                  </div>
                  <div className="stat-label">Error Rate</div>
                </div>
                <div className="stat-card stat-card-pass">
                  <div className="stat-number">{metrics['latencyP99'] ?? 0}ms</div>
                  <div className="stat-label">P99 Latency</div>
                </div>
              </div>
            )}

            {loading ? (
              <div className="card">
                <p>Loading alarms...</p>
              </div>
            ) : (
              <div className="alarm-grid">
                {alarms.map((alarm) => {
                  const stateClass = alarmStateClass(alarm.stateValue);
                  return (
                    <div key={alarm.alarmName} className={`alarm-card alarm-card--${stateClass}`}>
                      <div className="alarm-name">{alarm.alarmName}</div>
                      <div className={`alarm-state alarm-state--${stateClass}`}>
                        {alarm.stateValue}
                      </div>
                    </div>
                  );
                })}
                {alarms.length === 0 && (
                  <div
                    className="card"
                    style={{ gridColumn: '1 / -1', textAlign: 'center', padding: '2rem' }}
                  >
                    No alarms configured.
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </>
  );
}
